// David Walsh's debouncer
export function debounce(func, wait, immediate) {
  let timeout
  return function() {
    let context = this
    let args = arguments
    let later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export function paramsToObject(entries) {
  const result = {}
  for (const [key, value] of entries) {
    result[key] = value
  }
  return result
}

export function isUrlEncoded(param) {
  return decodeURIComponent(param) !== param
}

export function getMessageSearchHrefWithNewFilters(filtersToAdd) {
  /**
   * Adds the filters to the current url and returns it.
   *
   * For reference, the user wants to be able to right-click and open links in new tabs.
   * To be able to do this we override the filter handling that's done through the vuex store.
   * Why not just refactor filters to be handled through urls? Maybe in a later ticket.
   */
  let newUrl = new URL(window.location)
  let currentFilters = newUrl.searchParams.get('filters')
  let mappedFilters = {}

  if (currentFilters) {
    /**
     * Create object out of existing filters:
     * mappedFilters = {
     *    'filter_type1': ['filter_term1', 'filter_term2', ...],
     *    'filter_type2': [...],
     *    ...
     * }
     */
    currentFilters = decodeURI(currentFilters)
    let splitCurrentFilters = currentFilters.split(',') // filters are comma separated (after being decoded)
    splitCurrentFilters.forEach(splitFilter => {
      // filters in the url are in the form of 'filter_term:filter_value1&filter_value2'
      let keyValues = splitFilter.split(':', 2)
      mappedFilters[keyValues[0]] = keyValues[1].split('&')
    })
  }

  filtersToAdd.forEach(filter => {
    // the '+' doesn't get added to the key fsr
    let filterKey = filter.mode === '+' ? filter.type : `${filter.mode}${filter.type}`
    let filterValue = String(filter.term)
    let filterValues = mappedFilters && mappedFilters[filterKey] ? mappedFilters[filterKey] : []
    if (filterValues.indexOf(filterValue) < 0) {
      filterValues.push(filterValue) // add the new filter
    }
    mappedFilters[filterKey] = filterValues
  })

  // encode filters into new url
  let filterParams = []
  for (const [key, values] of Object.entries(mappedFilters)) {
    filterParams.push(`${key}:${values.join('&')}`)
  }
  newUrl.searchParams.set('filters', filterParams.join(','))
  return newUrl
}

export function getMonthName(monthNumber, abbreviated=true) {
  if (abbreviated) {
    let monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return monthNames[monthNumber - 1]
  } else {
    let monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    return monthNames[monthNumber - 1]
  }
}

const rolePrecedence = {
  'System Administrator': 10,
  'Administrator': 8,
  'Global Moderator': 6,
  'Moderator': 4
}

export function getRolePrecedence(siteUserRole) {
  return rolePrecedence[siteUserRole] || 1
}


export function makeSourcesCopyable(message) {
  /**
   * Alter the message to make the content safe to view and copy.
   *
   * @param message The unsafe message to alter.
   * @return {string} The safe and copyable message.
   */
  let preservedMessage = preserveContentTags(message)
  let parser = new DOMParser()
  let parsedMessage = parser.parseFromString(preservedMessage, 'text/html')

  for (let img of parsedMessage.images) {
    mangleSource(img)
  }
  for (let link of parsedMessage.links) {
    mangleSource(link)
  }

  // loop through all tags to remove any attributes we don't like
  for (let tag of parsedMessage.getElementsByTagName('*')) {
    if (tag.tagName.toUpperCase() === 'IFRAME') {
      mangleIframe(tag)
    }
    removeAttributes(tag)
  }

  for (let video of Array.from(parsedMessage.getElementsByTagName('video'))) {
    changeVideoTag(video)
  }
  for (let audio of Array.from(parsedMessage.getElementsByTagName('audio'))) {
    changeAudioTag(audio)
  }

  // details tags are usually closed. open them to show inner tags.
  for (let detailsTag of Array.from(parsedMessage.getElementsByTagName('details'))) {
    detailsTag.open = true
  }

  return new XMLSerializer().serializeToString(parsedMessage)
}

function preserveContentTags(message) {
  /**
   * Sometimes there's content in messages that are placed in angle brackets and we want to preserve these.
   * They're dubbed 'content tags'. e.g. '<secret message>'.
   *
   * 'Content tags' are any non-void element contained within the angle brackets `< >` and don't begin or end
   * with a `/`.
   *
   * @param message The message to search for content tags.
   * @return {string} The message with preserved content tags.
   */
  const tagsRe = /<\s*[^\/<>]+\s*>/g
  // void elements are tags that don't need to be closed
  const voidElements = new Set([
    'area', 'base', 'br', 'col', 'embed',
    'hr', 'img', 'input', 'link', 'meta',
    'param', 'source', 'track', 'wbr'
  ])

  let tagMatchRes = message.match(tagsRe)
  if (tagMatchRes) {
    for (let i = 0; i < tagMatchRes.length; i++) {
      let tagMatch = tagMatchRes[i]

      let tagOnly = tagMatchRes[i].slice(1, -1)  // remove angle brackets
          .trim()  // remove leading or trailing whitespaces within brackets
          .split(/\s/, 1)[0]  // slice off anything after the first whitespace char to get only the tag name

      // we don't want to preserve the void elements
      if (voidElements.has(tagOnly)) {
        continue
      }

      // search for a closing tag
      const closingTagsRe = new RegExp('<\/\s*' + tagOnly + '\s*>', )
      let closingTagMatchRes = message.match(closingTagsRe)
      // if there's a closing tag then it's a regular element that we don't want to preserve
      if (!closingTagMatchRes) {
        // we replace the `<` with `&lt;` and the `>` with `&gt;` to preserve the content
        let replaceTag = tagMatch.replace('<', '&lt;').replace('>', '&gt;')
        message = message.replace(tagMatch, replaceTag)
      }
    }
  }
  return message
}

function removeAttributes(element) {
  /**
   * Remove the style and other attributes of the element.
   *
   * @param element The html element to strip of attributes.
   * @return {HTMLElement} The element with it's attributes stripped.
   */
  if (element.style.backgroundImage) {
    // if we have a background image url, create a new empty image so that there is something to hover over/see
    // a bit of nastiness to remove url() from this property...
    let sourceString = element.style.backgroundImage.slice(4, -1).replace(/"/g, '')
    // If the src/href doesn't share a base url with the Arthropod:
    if (!sourceString.startsWith(window.location.origin)) {
      let imgElement = createFakeImage(sourceString, 'Copy background image link: ')
      element.appendChild(imgElement)
    }
  }
  element.style.cssText = '' // just erase all styles, we don't need 'em
}

function mangleSource(element) {
  /**
   * Mangle a source DOM element in-place.
   *
   * @param element The HTML element to mangle.
   */
  let elSrc = element.currentSrc || element.src || ''
  let elHref = element.href || ''

  if (elSrc) {
    element.src = '#'
  }
  if (element.srcset) {
    // Just nuke it - if this attr exists, we'll have already stored the path
    // that the browser would have selected with element.currentSrc above
    element.srcset = '#'
  }
  if (elHref) {
    element.href = '#'
  }

  let sourceString = elSrc || elHref

  if (!sourceString.startsWith(window.location.origin)) {
    // If the src/href doesn't share a base url with the Arthropod:
    element.setAttribute('data-copy', sourceString)
    element.setAttribute('title', `Copy link: ${sourceString}`)
    element.setAttribute('aria-label', `Copy link: ${sourceString}`)
  }
}

function changeAudioTag(element) {
  /**
   * Change audio tags into friendly and viewable content in place.
   *
   * @param element The audio element to change.
   */
  let audioSource = element.src
  if (audioSource === '') {
    audioSource = element.children[0].src
  }

  let hasSourceElement = false
  let replaceElement = document.createElement('div')
  for (let sourceElement of Array.from(element.getElementsByTagName('source'))) {
    // create anchor elements to preserve the sources.
    // otherwise we lose them when there's more than 1 <source> element.
    hasSourceElement = true
    let aElement = document.createElement('a')
    aElement.innerHTML = sourceElement.src + ' (audio tag)'
    aElement.setAttribute('href', '#')
    aElement.setAttribute('data-copy', sourceElement.src)
    aElement.setAttribute('title', `Copy link: ${sourceElement.src}`)
    aElement.setAttribute('aria-label', `Copy link: ${sourceElement.src}`)
    replaceElement.appendChild(aElement)
    element.removeChild(sourceElement)
  }

  if (!hasSourceElement) {
    let aElement = document.createElement('a')
    aElement.innerHTML = audioSource + ' (audio tag)'
    aElement.setAttribute('href', '#')
    aElement.setAttribute('data-copy', audioSource)
    aElement.setAttribute('title', `Copy link: ${audioSource}`)
    aElement.setAttribute('aria-label', `Copy link: ${audioSource}`)
    replaceElement.appendChild(aElement)
  }
  element.parentNode.replaceChild(replaceElement, element)
}

function changeVideoTag(element) {
  /**
   * Change video tags into friendly and viewable content in place.
   *
   * @param element The video element to change.
   */
  if (element.poster) {
    let posterElement = createFakeImage(element.poster, 'Copy poster image link: ')
    element.appendChild(posterElement)
    element.poster = '#'
  }

  // Get video tag src
  let videoSrc = element.src
  if (videoSrc === '') { // use <source> tag's(child) src if video tag does not have src
    videoSrc = element.children[0].src
  }

  // look for source(s)
  for (let videoSource of Array.from(element.getElementsByTagName('source'))) {
    let sourceElement = createFakeImage(videoSource.src, 'Copy video source link: ')
    element.appendChild(sourceElement)
    element.removeChild(videoSource)
  }

  let aElement = document.createElement('a')
  aElement.innerHTML = videoSrc + ' (video tag)'
  aElement.setAttribute('href', '#')
  aElement.setAttribute('data-copy', videoSrc)
  aElement.setAttribute('title', `Copy link: ${videoSrc}`)
  aElement.setAttribute('aria-label', `Copy link: ${videoSrc}`)
  element.parentNode.replaceChild(aElement, element)
}

function mangleIframe(element) {
  /**
   * Mangle an iframe element in-place.
   *
   * @param element The iframe element to mangle.
   */
  let frameSrc = element.src

  let frameInfo = document.createElement('a')
  frameInfo.innerHTML = frameSrc

  if (!frameSrc.startsWith(window.location.origin)) {
    frameInfo.setAttribute('href', '#')
    // If the src/href doesn't share a base url with the Arthropod:
    frameInfo.setAttribute('data-copy', frameSrc)
    frameInfo.setAttribute('title', `Copy link: ${frameSrc}`)
    frameInfo.setAttribute('aria-label', `Copy link: ${frameSrc}`)
  }

  element.parentNode.replaceChild(frameInfo, element)
}

function createFakeImage(url, textPrefix) {
  /**
   * Create a fake image element.
   *
   * @param url The source URL to make copyable on the fake image.
   * @param textPrefix The text show when user hovers over the fake image.
   * @return {HTMLImageElement} The fake image element.
   */
  let imgElement = document.createElement('img')
  imgElement.src = '#'
  imgElement.setAttribute('data-copy', url)
  imgElement.setAttribute('title', textPrefix + url)
  imgElement.setAttribute('aria-label', textPrefix + url)
  return imgElement
}
